/**
 * metricDetails — minimal per-metric metadata for the MetricDetail modal.
 *
 * STRIPPED 2026-04-28 per CEO directive (no mock data anywhere). All trend /
 * mix / contributors / breakdown values were realistic but fabricated; they're
 * gone now. Each entry holds ONLY:
 *
 *   { title, accent, parentPage, parentLabel, source }
 *
 * The hero value rendered in the modal is overlaid from live data passed in
 * via MetricDetail props (`liveValue`, `liveDelta`, etc.). Until Stage 2c
 * (backend extension that returns real top-N contributors + monthly trend +
 * mix per metric), the modal renders a clear "Deeper drill-in coming" card
 * instead of any fabricated chart or leaderboard.
 */

export const METRIC_DETAILS = {
  // ── Fans page hero tiles ────────────────────────────────────
  "total-fans": {
    title: "Total fans",
    accent: "#FC8019",
    parentPage: "fans", parentLabel: "Fans",
    source: {
      tables: ["users (Postgres)", "user_referral (Postgres)"],
      query: "COUNT(*) WHERE role_id=2 + login_provider mix + top referrer creators",
      rows: null, refreshed: "live",
    },
  },
  "active-fans-window": {
    title: "Active fans · window",
    accent: "#7C4DFF",
    parentPage: "fans", parentLabel: "Fans",
    source: {
      tables: ["celebrity_slot_booking (Postgres)", "users (Postgres)"],
      query: "DISTINCT user_id WHERE status != 'available' + engagement tier mix",
      rows: null, refreshed: "live",
    },
  },
  "paying-fans-window": {
    title: "Paying fans · window",
    accent: "#4CAF50",
    parentPage: "fans", parentLabel: "Fans",
    source: {
      tables: ["transaction_history (Postgres)", "users (Postgres)"],
      query: "DISTINCT user_id WHERE is_paid='success' AND celebrity_id IS NOT NULL + spend bucket mix + top spenders",
      rows: null, refreshed: "live",
    },
  },
  "vip-fans-window": {
    title: "VIP fans (21+ calls) · window",
    accent: "#FF6B9D",
    parentPage: "fans", parentLabel: "Fans",
    source: {
      tables: ["celebrity_slot_booking (Postgres)", "transaction_history (Postgres)"],
      query: "fans HAVING ≥21 calls in window + VIP-share-of-revenue mix + top VIPs",
      rows: null, refreshed: "live",
    },
  },
  // ── Home hero tiles ─────────────────────────────────────────
  "revenue-today": {
    title: "Revenue today",
    accent: "#FC8019",
    parentPage: "money", parentLabel: "Money",
    source: {
      tables: ["transaction_history (Postgres)", "brand_transaction_history (Postgres)"],
      query: "SUM(total_amount) WHERE is_paid='success' AND created_at = today_ist",
      rows: null,
      refreshed: "live",
    },
  },
  "fans-joined-today": {
    title: "Fans joined today",
    accent: "#7C4DFF",
    parentPage: "fans", parentLabel: "Fans",
    source: {
      tables: ["users (Postgres)"],
      query: "COUNT(*) WHERE role_id=2 AND created_at = today_ist",
      rows: null, refreshed: "live",
    },
  },
  "creators-joined-today": {
    title: "Creators joined today",
    accent: "#4CAF50",
    parentPage: "creators", parentLabel: "Creators",
    source: {
      tables: ["users (Postgres)"],
      query: "COUNT(*) WHERE role_id=3 AND created_at = today_ist",
      rows: null, refreshed: "live",
    },
  },
  "brands-joined-today": {
    title: "Brands joined today",
    accent: "#FF6B9D",
    parentPage: "brands", parentLabel: "Brands",
    source: {
      tables: ["users (Postgres)"],
      query: "COUNT(*) WHERE role_id=4 AND created_at = today_ist",
      rows: null, refreshed: "live",
    },
  },

  // ── Investor Data §1 — Scale (lifetime totals) ──────────────
  "brand-deals-lifetime": {
    title: "Brand deals closed",
    accent: "#7C4DFF",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["campaign_selected_creators (Postgres)"],
      query: "COUNT(*) WHERE status='completed' AND deleted_at IS NULL",
      rows: null, refreshed: "live",
    },
  },
  "gmv-lifetime": {
    title: "Total revenue (GMV)",
    accent: "#FC8019",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["transaction_history (Postgres)", "brand_transaction_history (Postgres)"],
      query: "fan-side: SUM(total_amount) WHERE is_paid='success' AND is_refund=false AND is_add_credit=false AND celebrity_id IS NOT NULL. brand-side: SUM(amount) WHERE transaction_type='wallet_deposit' AND is_credited=true AND payment_status='success'. Both windowed when pill applied.",
      rows: null, refreshed: "live",
    },
  },
  "creator-payouts-lifetime": {
    title: "Total paid to creators",
    accent: "#4CAF50",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["withdraw_requests (Postgres)", "brand_withdraw_requests (Postgres)"],
      query: "SUM(amount) WHERE status='completed' AND deleted_at IS NULL",
      rows: null, refreshed: "daily snapshot",
    },
  },
  "refunds-lifetime": {
    title: "Refunds issued (fan-initiated)",
    accent: "#FF6B9D",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["refund_requests (Postgres)"],
      query: "SUM(refund_amount) WHERE refund_status='approved' AND deleted_at IS NULL",
      rows: null, refreshed: "daily snapshot",
    },
  },

  // ── Investor Data §2 — Money on platform ────────────────────
  "wallet-fans": {
    title: "Money in Fan wallets",
    accent: "#FC8019",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["wallets (Postgres)", "users (Postgres)"],
      query: "SUM(w.balance) JOIN users WHERE u.role_id=2",
      rows: null, refreshed: "5-min cache",
    },
  },
  "wallet-creators": {
    title: "Money in Creator wallets",
    accent: "#4CAF50",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["wallets (Postgres)", "users (Postgres)"],
      query: "SUM(w.balance) JOIN users WHERE u.role_id=3",
      rows: null, refreshed: "5-min cache",
    },
  },
  "wallet-brands": {
    title: "Money in Brand wallets",
    accent: "#FF6B9D",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["brand_wallet (Postgres)"],
      query: "SUM(balance) WHERE deleted_at IS NULL",
      rows: null, refreshed: "5-min cache",
    },
  },
  "net-float": {
    title: "Net float Fancall holds",
    accent: "#7C4DFF",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["wallets (Postgres)", "brand_wallet (Postgres)", "users (Postgres)"],
      query: "fanWallet + brandWallet + creatorWallet — total platform liability across all 3 wallet pots (was incorrectly subtracting creatorWallet earlier)",
      rows: null, refreshed: "5-min cache",
    },
  },

  // ── Investor Data §5 — Virality ─────────────────────────────
  "creators-brought-fans": {
    title: "Fans brought by creators",
    accent: "#FC8019",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["user_referral (Postgres)"],
      query: "COUNT DISTINCT(referred_user) WHERE referee_role=3 AND referred_role=2 AND both != -7",
      rows: null, refreshed: "live",
    },
  },
  "fans-brought-fans": {
    title: "Fans brought by fans (K-factor input)",
    accent: "#7C4DFF",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["user_referral (Postgres)"],
      query: "COUNT DISTINCT(referred_user) WHERE referee_role=2 AND referred_role=2 AND both != -7",
      rows: null, refreshed: "live",
    },
  },
  "creators-brought-creators": {
    title: "Creators brought by creators",
    accent: "#4CAF50",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["user_referral (Postgres)"],
      query: "COUNT DISTINCT(referred_user) WHERE referee_role=3 AND referred_role=3 AND both != -7",
      rows: null, refreshed: "live",
    },
  },
  "viral-coefficient": {
    title: "K-factor (viral coefficient)",
    accent: "#FF6B9D",
    parentPage: "investor", parentLabel: "Investor data",
    source: {
      tables: ["user_referral (Postgres)", "users (Postgres)"],
      query: "DISTINCT(referred_user) ÷ COUNT(users) — both filtered for role_id != -7",
      rows: null, refreshed: "live",
    },
  },
};

export const formatINR_ = (n) => {
  if (typeof n !== "number") return n;
  if (n >= 1e7) return "Rs " + (n / 1e7).toFixed(2) + "Cr";
  if (n >= 1e5) return "Rs " + (n / 1e5).toFixed(2) + "L";
  if (n >= 1e3) return "Rs " + (n / 1e3).toFixed(1) + "K";
  return "Rs " + n;
};
export const formatNum_ = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n);
